// I piani delle sottoscrizioni nelle card (PlanSheet): tutto quello che la card
// scrive arriva dai dati dell'API, nessuna immagine con nome e prezzo disegnati.
//  - condizioni: per ogni servizio quanti ne comprende all'anno, quanto costa e
//    il prezzo generale, calcolati dall'API con le regole della prenotazione;
//  - dettaglio: l'HTML scritto dalla migration del listino (etichetta, servizi,
//    nota), che qui si legge a pezzi.

/**
 * Le formule dello stesso piano (stesso nome) diventano una card sola, dalla
 * più breve (mensile) alla più lunga. Due righe con lo stesso nome e lo stesso
 * periodo, come un'edizione vecchia e quella in vendita, non fanno due formule:
 * resta quella con lo slug preferito, se c'è, altrimenti la prima.
 */
export function raggruppaPiani(piani, slugPreferito = null) {
  const elenco = Array.isArray(piani) ? piani : Object.values(piani || {});
  const gruppi = [];
  const perNome = {};
  elenco.filter(Boolean).forEach((piano) => {
    const nome = String(piano.nome || "").trim().toLowerCase();
    if (!perNome[nome]) {
      perNome[nome] = [];
      gruppi.push(perNome[nome]);
    }
    const gruppo = perNome[nome];
    const stessoPeriodo = gruppo.findIndex(
      (p) => Number(p.mesi_frequenza_rinnovo) === Number(piano.mesi_frequenza_rinnovo)
    );
    if (stessoPeriodo === -1) {
      gruppo.push(piano);
    } else if (slugPreferito && piano.slug === slugPreferito) {
      gruppo.splice(stessoPeriodo, 1, piano);
    }
  });
  gruppi.forEach((gruppo) =>
    gruppo.sort((a, b) => Number(a.mesi_frequenza_rinnovo || 0) - Number(b.mesi_frequenza_rinnovo || 0))
  );
  return gruppi;
}

/**
 * Il dettaglio del piano in pezzi di testo con il solo grassetto: nessun HTML
 * dal database finisce nella pagina. L'etichetta sta in .piano-etichetta, ogni
 * servizio in un <li>, le note negli altri paragrafi. I servizi con
 * .piano-in-testa (il colloquio iniziale) aprono la card, prima delle condizioni. Un dettaglio scritto a
 * mano, senza questa forma, resta leggibile: i suoi paragrafi diventano note.
 */
export function leggiDettaglio(html) {
  const vuoto = { etichetta: null, inTesta: [], servizi: [], note: [] };
  if (!html || typeof DOMParser === "undefined") {
    return vuoto;
  }
  const documento = new DOMParser().parseFromString(String(html), "text/html");
  const pezzi = (nodo) =>
    Array.from(nodo.childNodes)
      .map((figlio) => ({
        testo: figlio.textContent || "",
        forte: figlio.nodeType === 1 && ["STRONG", "B"].includes(figlio.tagName),
      }))
      .filter((pezzo) => pezzo.testo !== "");
  const etichetta = documento.querySelector(".piano-etichetta");
  return {
    etichetta: etichetta ? etichetta.textContent.trim() : null,
    inTesta: Array.from(documento.querySelectorAll("li.piano-in-testa")).map(pezzi).filter((riga) => riga.length),
    servizi: Array.from(documento.querySelectorAll("li:not(.piano-in-testa)")).map(pezzi).filter((riga) => riga.length),
    note: Array.from(documento.querySelectorAll("p"))
      .filter((p) => !p.classList.contains("piano-etichetta"))
      .map(pezzi)
      .filter((nota) => nota.some((pezzo) => pezzo.testo.trim() !== "")),
  };
}

/**
 * Un importo in euro: 6 €, 4,92 €, 1.500 € (spazio unificatore prima dell'euro).
 * Il separatore delle migliaia è scritto qui: Intl in italiano non lo mette a
 * quattro cifre (1500), e il sito scrive 1.500.
 */
export function formattaPrezzo(valore, locale = "it", { spazio = true } = {}) {
  const numero = Math.round((Number(valore) || 0) * 100) / 100;
  const separatoreMigliaia = locale === "en" ? "," : ".";
  const separatoreDecimali = locale === "en" ? "." : ",";
  const intero = Math.trunc(Math.abs(numero))
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, separatoreMigliaia);
  const centesimi = Math.round((Math.abs(numero) % 1) * 100);
  const cifre =
    (numero < 0 ? "-" : "") + intero + (centesimi ? separatoreDecimali + String(centesimi).padStart(2, "0") : "");
  return cifre + (spazio ? " €" : "€");
}

/** "/ mese", "/ anno", "/ 3 mesi"; vuoto per il piano gratuito */
export function periodoDelPiano(piano, t) {
  const mesi = Number(piano && piano.mesi_frequenza_rinnovo);
  if (!mesi) {
    return "";
  }
  if (mesi === 1) {
    return t("PlanSheet.alMese");
  }
  if (mesi === 12) {
    return t("PlanSheet.allAnno");
  }
  return t("PlanSheet.ogniMesi", { n: mesi });
}

/** Il nome della formula nel selettore: Mensile, Annuale, Ogni 3 mesi */
export function formulaDelPiano(piano, t) {
  const mesi = Number(piano && piano.mesi_frequenza_rinnovo);
  if (mesi === 1) {
    return t("PlanSheet.mensile");
  }
  if (mesi === 12) {
    return t("PlanSheet.annuale");
  }
  return t("PlanSheet.ogniMesiFormula", { n: mesi });
}

/**
 * Le righe delle condizioni del piano: { chiave, valore, asterisco }. Una riga
 * compare solo se il piano dà qualcosa in più di chi non ha una sottoscrizione
 * (gratuiti nell'anno o un prezzo più basso): il resto costa come sempre. Con
 * tutte = true compaiono tutte, ed è la card del piano gratuito. L'asterisco
 * sta sulle visite: la prestazione si paga a parte, alla struttura.
 */
export function righeCondizioni(condizioni, t, locale = "it", { tutte = false } = {}) {
  if (!condizioni) {
    return [];
  }
  const euro = (valore) => formattaPrezzo(valore, locale);
  const conVantaggio = (c) => !!c && (Number(c.incluse) > 0 || Number(c.prezzo) < Number(c.prezzo_generale));
  const daMostrare = (c) => !!c && (tutte || conVantaggio(c));

  const prenotazioni = (c) => {
    if (Number(c.incluse) > 0 && Number(c.prezzo) > 0) {
      return t("PlanSheet.prenotazioniIncluseAllAnno", { n: c.incluse, prezzo: euro(c.prezzo) });
    }
    if (Number(c.prezzo) === 0) {
      return t("PlanSheet.prenotazioniIncluse");
    }
    return t("PlanSheet.aPrenotazione", { prezzo: euro(c.prezzo) });
  };
  const teleconsulti = (c, piuServizi) => {
    if (Number(c.incluse) > 0 && Number(c.prezzo) > 0) {
      const chiave = Number(c.incluse) === 1 ? "PlanSheet.unoInclusoAllAnno" : "PlanSheet.inclusiAllAnno";
      return t(chiave, { n: c.incluse, prezzo: euro(c.prezzo) });
    }
    if (Number(c.prezzo) === 0) {
      return t(piuServizi ? "PlanSheet.inclusi" : "PlanSheet.incluso");
    }
    return euro(c.prezzo);
  };
  const specialista = (c) => {
    if (Number(c.prezzo) === 0) {
      return t("PlanSheet.prenotazioneInclusa");
    }
    if (Number(c.incluse) > 0) {
      return t("PlanSheet.prenotazioniIncluseAllAnno", { n: c.incluse, prezzo: euro(c.prezzo) });
    }
    return t("PlanSheet.prenotazioneA", { prezzo: euro(c.prezzo) });
  };

  const righe = [];
  const visite = condizioni.prestazione;
  if (daMostrare(visite)) {
    righe.push({ chiave: t("PlanSheet.visite"), valore: prenotazioni(visite), asterisco: true });
  }
  const farmaci = condizioni.ordine;
  if (daMostrare(farmaci)) {
    righe.push({ chiave: t("PlanSheet.farmaci"), valore: prenotazioni(farmaci), asterisco: false });
  }
  const rapido = condizioni.emergenza;
  const pediatrico = condizioni.tc_pediatrico;
  const uguali =
    daMostrare(rapido) &&
    daMostrare(pediatrico) &&
    Number(rapido.incluse) === Number(pediatrico.incluse) &&
    Number(rapido.prezzo) === Number(pediatrico.prezzo);
  if (uguali) {
    righe.push({ chiave: t("PlanSheet.rapidoEPediatrico"), valore: teleconsulti(rapido, true), asterisco: false });
  } else {
    if (daMostrare(rapido)) {
      righe.push({ chiave: t("PlanSheet.rapido"), valore: teleconsulti(rapido, false), asterisco: false });
    }
    if (daMostrare(pediatrico)) {
      righe.push({ chiave: t("PlanSheet.pediatrico"), valore: teleconsulti(pediatrico, false), asterisco: false });
    }
  }
  const teleconsulto = condizioni.teleconsulto;
  if (daMostrare(teleconsulto)) {
    righe.push({ chiave: t("PlanSheet.specialista"), valore: specialista(teleconsulto), asterisco: false });
  }
  return righe;
}
